/**
 * Live-Update Hooks for Planning Widgets
 *
 * These hooks connect individual widgets to the WebSocket event stream,
 * automatically invalidating React Query caches or providing callback-based
 * updates when relevant events arrive from the backend.
 *
 * Usage pattern:
 *   const { lastUpdate } = useSyncLiveUpdates();           // in SyncStatusWidget
 *   const { lastUpdate } = useScenarioLiveUpdates();       // in PlanningScenarioWidget
 *   const { lastAlert } = useConflictLiveAlerts();         // in ConflictListWidget
 *   const { progress } = useSyncProgress();                // in progress indicators
 *   const { lastWriteback } = useWritebackLiveUpdates();   // in write-back UI
 *
 * @see WebSocketContext.tsx — Connection management
 * @see planning/signals.py — Backend emit_* functions
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWebSocket,
  type SyncUpdateData,
  type ScenarioUpdateData,
  type ConflictAlertData,
  type WritebackUpdateData,
  type SyncProgressData,
  type NotificationData,
} from '../contexts/WebSocketContext';
import { useAppSettings } from '../contexts/AppSettingsContext';
import type { AlertThreshold } from '../contexts/AppSettingsContext';

// ============================================================================
// useSyncLiveUpdates — For SyncStatusWidget and data-dependent widgets
// ============================================================================

export interface UseSyncLiveUpdatesResult {
  /** Most recent sync update event */
  lastUpdate: SyncUpdateData | null;
  /** Number of sync updates received in this session */
  updateCount: number;
}

/**
 * Hook that listens for sync_update events and auto-invalidates relevant queries.
 * Use in widgets that display synced data (Gantt, Heatmap, Tables).
 *
 * @param entityFilter - Optional: only react to specific entity types (e.g., 'ProductionOrder')
 */
export function useSyncLiveUpdates(
  entityFilter?: string | string[]
): UseSyncLiveUpdatesResult {
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<SyncUpdateData | null>(null);
  const [updateCount, setUpdateCount] = useState(0);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe<SyncUpdateData>(
      'sync_update',
      (data) => {
        // Filter by entity type if specified
        if (entityFilter) {
          const filters = Array.isArray(entityFilter)
            ? entityFilter
            : [entityFilter];
          if (
            !filters.some(
              (f) =>
                data.entity_type.toLowerCase() === f.toLowerCase()
            )
          ) {
            return;
          }
        }

        setLastUpdate(data);
        setUpdateCount((prev) => prev + 1);

        // Invalidate relevant React Query caches based on entity type
        const entity = data.entity_type.toLowerCase();
        const invalidations: string[][] = [];

        switch (entity) {
          case 'productionorder':
          case 'operation':
            invalidations.push(
              ['gantt-data'],
              ['planning-scenarios'],
              ['scenario-comparison'],
              ['capacity-data']
            );
            break;
          case 'workcenter':
          case 'employee':
            invalidations.push(
              ['capacity-data'],
              ['gantt-data']
            );
            break;
          case 'item':
          case 'bomline':
            invalidations.push(['planning-scenarios']);
            break;
          case 'machinecapacitygroup':
          case 'machinenumber':
            invalidations.push(
              ['machine-capacity-groups'],
              ['machines'],
              ['capacity-data']
            );
            break;
          case 'machineoperation':
          case 'machineoperationquantity':
            invalidations.push(
              ['machine-operations'],
              ['machine-operation-quantities'],
              ['machines'],
              ['gantt-data']
            );
            break;
          case 'calendarheader':
          case 'calendartimewindow':
          case 'calendarexception':
            invalidations.push(
              ['calendar-headers'],
              ['calendar-time-windows'],
              ['calendar-exceptions'],
              ['capacity-data'],
              ['gantt-data']
            );
            break;
          case 'workcenterCalendarassignment':
            invalidations.push(
              ['wc-calendar-assignments'],
              ['calendar-headers'],
              ['capacity-data']
            );
            break;
          // Phase 8 — Scheduling entities
          case 'schedulingresult':
          case 'scheduledoperation':
            invalidations.push(
              ['gantt-data'],
              ['capacity-data'],
              ['planning-scenarios'],
              ['scheduling']
            );
            break;
          case 'reschedule':
            invalidations.push(
              ['gantt-data'],
              ['capacity-data'],
              ['machine-operations'],
              ['planning-scenarios']
            );
            break;
          // Phase 10 — KPI / Site / Cost entities
          case 'kpisnapshot':
            invalidations.push(
              ['kpi']
            );
            break;
          case 'site':
            invalidations.push(
              ['sites'],
              ['cross-site'],
              ['kpi']
            );
            break;
          case 'materialcost':
            invalidations.push(
              ['material-costs'],
              ['kpi']
            );
            break;
          default:
            // Invalidate all planning queries for unknown entity types
            invalidations.push(['planning']);
            break;
        }

        // Also always invalidate sync status
        invalidations.push(['planning-sync']);

        invalidations.forEach((key) => {
          queryClient.invalidateQueries({ queryKey: key });
        });

        console.log(
          `[WS] Sync update: ${data.entity_type} — ` +
            `${data.records_created} created, ${data.records_updated} updated`
        );
      }
    );

    return unsubscribe;
  }, [isConnected, subscribe, queryClient, entityFilter]);

  return { lastUpdate, updateCount };
}

// ============================================================================
// useScenarioLiveUpdates — For PlanningScenarioWidget and ScenarioDetailPage
// ============================================================================

export interface UseScenarioLiveUpdatesResult {
  /** Most recent scenario status change */
  lastUpdate: ScenarioUpdateData | null;
  /** Number of scenario updates received */
  updateCount: number;
}

/**
 * Hook that listens for scenario_update events.
 * Auto-invalidates scenario queries when status changes.
 *
 * @param scenarioId - Optional: only react to a specific scenario
 */
export function useScenarioLiveUpdates(
  scenarioId?: number | string
): UseScenarioLiveUpdatesResult {
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [lastUpdate, setLastUpdate] = useState<ScenarioUpdateData | null>(
    null
  );
  const [updateCount, setUpdateCount] = useState(0);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe<ScenarioUpdateData>(
      'scenario_update',
      (data) => {
        // Filter by scenario ID if specified
        if (
          scenarioId !== undefined &&
          String(data.scenario_id) !== String(scenarioId)
        ) {
          return;
        }

        setLastUpdate(data);
        setUpdateCount((prev) => prev + 1);

        // Invalidate scenario-related queries
        queryClient.invalidateQueries({
          queryKey: ['planning-scenarios'],
        });
        queryClient.invalidateQueries({
          queryKey: ['scenario-detail', String(data.scenario_id)],
        });
        queryClient.invalidateQueries({
          queryKey: ['scenario-audit-trail', String(data.scenario_id)],
        });

        // If scenario moved to pending_review or approved, refresh gantt
        if (
          data.new_status === 'pending_review' ||
          data.new_status === 'approved'
        ) {
          queryClient.invalidateQueries({ queryKey: ['gantt-data'] });
          queryClient.invalidateQueries({
            queryKey: ['scenario-comparison'],
          });
        }

        console.log(
          `[WS] Scenario update: "${data.scenario_name}" ` +
            `${data.old_status} → ${data.new_status}`
        );
      }
    );

    return unsubscribe;
  }, [isConnected, subscribe, queryClient, scenarioId]);

  return { lastUpdate, updateCount };
}

// ============================================================================
// useConflictLiveAlerts — For ConflictListWidget
// ============================================================================

export interface UseConflictLiveAlertsResult {
  /** Most recent conflict alert */
  lastAlert: ConflictAlertData | null;
  /** All alerts received in this session */
  alerts: ConflictAlertData[];
  /** Clear the alerts buffer */
  clearAlerts: () => void;
}

/**
 * Hook that listens for conflict_alert events.
 * Buffers alerts for display and auto-invalidates conflict queries.
 */
export function useConflictLiveAlerts(): UseConflictLiveAlertsResult {
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [lastAlert, setLastAlert] = useState<ConflictAlertData | null>(null);
  const [alerts, setAlerts] = useState<ConflictAlertData[]>([]);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
    setLastAlert(null);
  }, []);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe<ConflictAlertData>(
      'conflict_alert',
      (data) => {
        setLastAlert(data);
        setAlerts((prev) => [data, ...prev].slice(0, 100));

        // Invalidate conflict queries
        queryClient.invalidateQueries({ queryKey: ['conflicts'] });
        queryClient.invalidateQueries({
          queryKey: ['planning-scenarios'],
        });

        console.log(
          `[WS] Conflict alert: [${data.severity}] ${data.conflict_type} — ${data.description.slice(0, 80)}`
        );
      }
    );

    return unsubscribe;
  }, [isConnected, subscribe, queryClient]);

  return { lastAlert, alerts, clearAlerts };
}

// ============================================================================
// useWritebackLiveUpdates — For Write-Back status display
// ============================================================================

export interface UseWritebackLiveUpdatesResult {
  /** Most recent write-back event */
  lastWriteback: WritebackUpdateData | null;
  /** Whether a write-back is currently in progress */
  isWriting: boolean;
}

/**
 * Hook that listens for writeback_update events.
 * Tracks write-back status for UI indicators.
 */
export function useWritebackLiveUpdates(): UseWritebackLiveUpdatesResult {
  const { subscribe, isConnected } = useWebSocket();
  const queryClient = useQueryClient();
  const [lastWriteback, setLastWriteback] =
    useState<WritebackUpdateData | null>(null);
  const [isWriting, setIsWriting] = useState(false);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe<WritebackUpdateData>(
      'writeback_update',
      (data) => {
        setLastWriteback(data);
        setIsWriting(
          data.status !== 'completed' && data.status !== 'failed'
        );

        // Invalidate scenario when write-back completes
        queryClient.invalidateQueries({
          queryKey: ['scenario-detail', String(data.scenario_id)],
        });
        queryClient.invalidateQueries({
          queryKey: ['planning-scenarios'],
        });

        console.log(
          `[WS] Write-back: scenario ${data.scenario_id} — ${data.status} ` +
            `(${data.operations_written} written, ${data.operations_failed} failed)`
        );
      }
    );

    return unsubscribe;
  }, [isConnected, subscribe, queryClient]);

  return { lastWriteback, isWriting };
}

// ============================================================================
// useSyncProgress — For sync progress indicators
// ============================================================================

export interface UseSyncProgressResult {
  /** Current sync progress (null if no sync active) */
  progress: SyncProgressData | null;
  /** Whether a sync is currently in progress */
  isSyncing: boolean;
}

/**
 * Hook that listens for sync_progress events.
 * Tracks real-time sync progress for progress bars.
 *
 * @param entityFilter - Optional: only track specific entity types
 */
export function useSyncProgress(
  entityFilter?: string
): UseSyncProgressResult {
  const { subscribe, isConnected } = useWebSocket();
  const [progress, setProgress] = useState<SyncProgressData | null>(null);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    if (!isConnected) return;

    const unsubscribe = subscribe<SyncProgressData>(
      'sync_progress',
      (data) => {
        if (
          entityFilter &&
          data.entity_type.toLowerCase() !== entityFilter.toLowerCase()
        ) {
          return;
        }

        setProgress(data);
        setIsSyncing(data.progress_pct < 100);

        // Clear progress after completion (with short delay for UI)
        if (data.progress_pct >= 100) {
          setTimeout(() => {
            setProgress(null);
            setIsSyncing(false);
          }, 2000);
        }
      }
    );

    return unsubscribe;
  }, [isConnected, subscribe, entityFilter]);

  return { progress, isSyncing };
}

// ============================================================================
// useNotificationLive — For notification bell / toast system
// ============================================================================

export interface UseNotificationLiveResult {
  /** Notifications filtered by the user's alert threshold preference */
  notifications: NotificationData[];
  /** Number of unread notifications */
  unreadCount: number;
  /** Mark all notifications as read */
  markAllRead: () => void;
  /** Clear all notifications */
  clearAll: () => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const LEVEL_PRIORITY: Record<string, number> = { info: 0, warning: 1, error: 2 };
const THRESHOLD_MIN: Record<AlertThreshold, number> = { all: 0, warning: 1, error: 2 };

function passesThreshold(level: string, threshold: AlertThreshold): boolean {
  return (LEVEL_PRIORITY[level] ?? 0) >= THRESHOLD_MIN[threshold];
}

/** Fire a Web Notification (non-blocking, permission already checked). */
function fireBrowserNotification(n: NotificationData) {
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    new Notification(n.title, { body: n.message, icon: '/favicon.ico', tag: n.timestamp });
  } catch {
    // Silent — some browsers block from service-worker context
  }
}

/** Play a short beep via Web Audio API. */
function playAlertSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 660;
    gain.gain.value = 0.15;
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.stop(ctx.currentTime + 0.3);
  } catch {
    // AudioContext not available
  }
}

/**
 * Hook that provides access to WebSocket notifications
 * for display in notification bell, toast popups, etc.
 *
 * Respects user preferences from AppSettings:
 * - alertThreshold: filters which notifications are shown
 * - browserNotifications: fires desktop Notification API
 * - soundAlerts: plays a short beep for incoming alerts
 */
export function useNotificationLive(): UseNotificationLiveResult {
  const { notifications: allNotifications, clearNotifications } = useWebSocket();
  const { alertThreshold, browserNotifications, soundAlerts } = useAppSettings();
  const [readCount, setReadCount] = useState(0);
  const prevLenRef = useRef(0);

  // Filter notifications by threshold
  const notifications = useMemo(
    () => allNotifications.filter((n) => passesThreshold(n.level, alertThreshold)),
    [allNotifications, alertThreshold],
  );

  const unreadCount = Math.max(0, notifications.length - readCount);

  // Side-effects for NEW notifications
  useEffect(() => {
    if (notifications.length <= prevLenRef.current) {
      prevLenRef.current = notifications.length;
      return;
    }
    // There are new notifications (prepended at index 0)
    const newCount = notifications.length - prevLenRef.current;
    prevLenRef.current = notifications.length;

    for (let i = 0; i < newCount; i++) {
      const n = notifications[i];
      if (browserNotifications) fireBrowserNotification(n);
    }
    if (soundAlerts && newCount > 0) playAlertSound();
  }, [notifications.length, browserNotifications, soundAlerts, notifications]);

  const markAllRead = useCallback(() => {
    setReadCount(notifications.length);
  }, [notifications.length]);

  const clearAll = useCallback(() => {
    clearNotifications();
    setReadCount(0);
    prevLenRef.current = 0;
  }, [clearNotifications]);

  return {
    notifications,
    unreadCount,
    markAllRead,
    clearAll,
  };
}
